import api from '../config/api';
api.cacheSite(true);

// Routen, auf denen der Service Worker deaktiviert werden soll, damit OAuth-Redirects
// (z.B. Yandex-ID) nicht durch SW-Nav-Handling/Updates gestört werden.
export const AUTH_SW_BYPASS_ROUTES = new Set([
    '/login',
    '/register',
    '/verify-email',
    '/forgot-password',
    '/reset-password'
]);

export const shouldBypassServiceWorkerForPath = (pathname) => {
    try {
        return AUTH_SW_BYPASS_ROUTES.has(String(pathname || ''));
    } catch (_) {
        return false;
    }
};

/**
 * Best-effort: deregistriert Service Worker und löscht alle artistix-* Caches.
 * Wird auf Auth-Seiten aufgerufen, um Chrome zuverlässig ohne SW dazwischen zu halten.
 */
export const hardDisableServiceWorker = async () => {
    if (typeof window === 'undefined') return;

    // Unregister all SW registrations
    try {
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(
                (regs || []).map(async (r) => {
                    try { await r.unregister(); } catch (_) { }
                })
            );
        }
    } catch (_) { }

    // Clear caches
    try {
        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(
                (keys || [])
                    .filter((k) => String(k).startsWith('artistix-') || String(k).startsWith('artistix'))
                    .map((k) => {
                        try { return caches.delete(k); } catch (_) { return false; }
                    })
            );
        }
    } catch (_) { }
};

/**
 * During development, force-unregister existing service workers and skip registration
 * to avoid caching issues. In production we keep the SW to enable update flow.
 * This runs immediately on module import in the browser (dev only).
 */
if (typeof window !== 'undefined' && 'serviceWorker' in navigator && process.env.NODE_ENV !== 'production') {
    try {
        navigator.serviceWorker.getRegistrations().then((regs) => {
            regs.forEach((r) => {
                try { r.unregister(); } catch (e) { }
            });
        }).catch(() => { });
    } catch (e) { }
}

// Service Worker Registration
// Only register the service worker in production builds to avoid caching
// issues during development (prevents stale data and need for Ctrl+F5).
export const registerServiceWorker = async () => {
    if (process.env.NODE_ENV !== 'production') {
        // In development do not register the service worker.
        console.debug('Service Worker registration skipped in non-production environment.');
        return null;
    }

    // Zusätzlich: in Produktion NICHT registrieren, wenn wir uns auf Auth-Seiten befinden.
    // (Hier ist window.location stabil, weil es nur im Browser ausgeführt wird.)
    try {
        if (typeof window !== 'undefined' && shouldBypassServiceWorkerForPath(window.location?.pathname)) {
            console.debug('Service Worker registration skipped on auth route:', window.location?.pathname);
            return null;
        }
    } catch (_) { }

    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.register('/sw.js');
            console.log('Service Worker registered successfully:', registration);

            // If there's already a waiting worker (previous install), notify immediately
            if (registration.waiting) {
                console.log('Service Worker waiting -> dispatch sw:update');
                try {
                    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                } catch (e) {
                    // ignore
                }
                try {
                    window.dispatchEvent(new CustomEvent('sw:update', { detail: { registration } }));
                } catch (e) {
                    try { window.dispatchEvent(new Event('sw:update')); } catch (err) { }
                }
            }

            // Proactively check for updates once on registration to detect new builds
            try {
                registration.update().catch(() => { });
            } catch (e) {
                // ignore
            }

            // Check for updates (new worker installing)
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (!newWorker) return;
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New version available
                        console.log('New version available (updatefound)');
                        try {
                            newWorker.postMessage({ type: 'SKIP_WAITING' });
                        } catch (e) {
                            // ignore
                        }
                        try {
                            window.dispatchEvent(new CustomEvent('sw:update', { detail: { registration } }));
                        } catch (e) {
                            try { window.dispatchEvent(new Event('sw:update')); } catch (err) { }
                        }
                    }
                });
            });

            // Optional: log controller changes (for debugging)
            navigator.serviceWorker.addEventListener?.('controllerchange', () => {
                console.log('Service Worker controller changed');
            });

            return registration;
        } catch (error) {
            console.error('Service Worker registration failed:', error);
        }
    }
};

// Unregister Service Worker
export const unregisterServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.ready;
            await registration.unregister();
            console.log('Service Worker unregistered');
        } catch (error) {
            console.error('Service Worker unregistration failed:', error);
        }
    }
};

// Check if app is online
export const isOnline = () => {
    return navigator.onLine;
};

// Add online/offline listeners
export const setupOnlineStatus = (onOnline, onOffline) => {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
    };
};
